<template>
  <div class="menu-management app-page">
    <div class="tree-container">
      <div class="tree-toolbar">
        <el-input
          v-model="searchKey"
          placeholder="搜索菜单"
          clearable
          class="search-input"
          :prefix-icon="Search"
        />
        <div class="toolbar-actions">
          <el-button
            v-if="can('列表')"
            type="primary"
            circle
            size="small"
            class="app-refresh-btn"
            :icon="Refresh"
            @click="refreshTable()"
            title="刷新"
          />
          <el-button v-if="can('新增')" type="primary" :icon="Plus" @click="handleAddRoot">
            添加根菜单
          </el-button>
        </div>
      </div>
      <el-scrollbar class="tree-scrollbar">
        <el-table
          :key="tableRenderKey"
          ref="tableRef"
          :data="menuTree"
          row-key="Id"
          border
          default-expand-all
          :indent="20"
          lazy
          :load="load"
          :tree-props="{ children: 'Children', hasChildren: 'hasChildren' }"
          v-loading="loading"
          style="width: 100%"
        >
          <el-table-column prop="Name" label="菜单名称" min-width="200">
            <template #default="{ row }">
              <div class="table-cell-name">
                <el-icon v-if="row.IconNew" class="cell-icon">
                  <component :is="getIconComponent(row.IconNew)"></component>
                </el-icon>
                <span>{{ row.Name }}</span>
                <!-- <el-tag v-if="row.IsButton" size="small" type="warning" class="cell-tag">按钮</el-tag> -->
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="menuType" label="菜单类型" width="100" align="center" header-align="center">
            <template #default="{ row }">
              <el-tag :type="getMenuTypeType(row)">{{ getMenuTypeText(row) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="Code" label="路由" min-width="180" >
            <template #default="{ row }">
              <span class="cell-route">{{ row.Code || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="OrderSort" label="排序" width="90" align="center" header-align="center">
            <template #default="{ row }">
              <span>{{ row.OrderSort }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="Enabled" label="是否禁用" width="100" align="center" header-align="center">
            <template #default="{ row }">
              <el-tag :type="row.Enabled ? 'success' : 'danger'" size="small">
                {{ row.Enabled ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="IsHide" label="是否隐藏" width="100" align="center" header-align="center">
            <template #default="{ row }">
              <el-tag :type="row.IsHide ? 'info' : 'success'" size="small">
                {{ row.IsHide ? '隐藏' : '显示' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column v-if="canAny('新增', '编辑', '删除')" label="操作" width="200" fixed="right" align="center" header-align="center">
            <template #default="{ row }">
              <el-button v-if="can('新增')" type="primary" :icon="Plus" link size="small" @click="handleAddChild(row)">
                添加
              </el-button>
              <el-button v-if="can('编辑')" type="warning" :icon="Edit" link size="small" @click="handleEdit(row)">
                编辑
              </el-button>
              <el-button v-if="can('删除')" type="danger" :icon="Delete" link size="small" @click="handleDelete(row)">
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-scrollbar>
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      destroy-on-close
    >
      <el-form ref="formRef" :model="formData" :rules="formRules" label-width="100px">
        <el-form-item label="菜单类型">
          <el-radio-group v-model="menuType">
            <el-radio-button label="目录" />
            <el-radio-button label="页面" />
            <el-radio-button label="按钮" />
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="menuType === '按钮' ? '按钮名称' : '菜单名称'" prop="Name">
          <el-input v-model="formData.Name" :placeholder="menuType === '按钮' ? '请输入按钮名称' : '请输入菜单名称'" />
        </el-form-item>
        <el-form-item label="菜单路由" prop="Code" :rules="codeRules">
          <el-input v-model="formData.Code" placeholder="请输入页面组件路径，如：/ParameterSettings/BusinessScope/index.vue" :disabled="isCodeDisabled" />
        </el-form-item>
        <el-form-item label="图标">
          <el-select v-model="formData.IconNew" placeholder="请选择图标" clearable filterable class="w-full">
            <el-option
              v-for="(component, name) in iconOptions"
              :key="name"
              :label="name"
              :value="name"
            >
              <div style="display: flex; align-items: center; gap: 8px;">
                <el-icon><component :is="component" /></el-icon>
                <span>{{ name }}</span>
              </div>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="formData.OrderSort" :min="-100" :max="100" />
        </el-form-item>
        <el-form-item v-if="menuType !== '按钮'" label="是否按钮">
          <el-switch v-model="formData.IsButton" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="formData.Enabled" active-text="激活" inactive-text="禁用" />
        </el-form-item>
        <el-form-item label="是否隐藏">
          <el-switch v-model="formData.IsHide" />
        </el-form-item>
        <el-form-item label="接口选择">
          <el-select
            v-model="formData.Mid"
            placeholder="请选择接口"
            clearable
            filterable
            class="w-full"
            @visible-change="handleInterfaceSelectVisibleChange"
          >
            <el-option
              v-for="item in interfaceList"
              :key="item.Id"
              :label="item.Name"
              :value="item.Id"
            >
              <span style="float: left">{{ item.Name }}</span>
              <span style="float: right; color: #8492a6; font-size: 12px">{{ item.LinkUrl }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="是否缓存">
          <el-switch v-model="formData.IskeepAlive" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="formData.Description" type="textarea" :rows="3" placeholder="请输入描述" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button v-if="can('保存')" type="primary" :loading="saving" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, watch, computed, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Edit, Search, Delete, Refresh } from '@element-plus/icons-vue'
import { PermissionManagement } from '@/network/index'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import { useRouteTitle } from '@/utils/useRouteTitle'
import { useButtonPermission } from '@/utils/buttonPermission'

useRouteTitle('菜单管理')
const { can, canAny } = useButtonPermission('/QuanXianGuanLi/caidan.vue')

// 表格引用
const tableRef = ref<any>(null)
const searchKey = ref('')
// 记录当前刷新上下文（0=根菜单，其他=当前父菜单ID）
const currentRefreshParentId = ref<string | number>(0)
// 强制重建树表格，清空 el-table 懒加载缓存
const tableRenderKey = ref(0)

// 表格数据
const menuTree = ref<any[]>([])
const loading = ref(false)

// 对话框
const dialogVisible = ref(false)
const dialogMode = ref<'create' | 'edit' | 'addChild'>('create')
const saving = ref(false)
const formRef = ref<any>(null)
const currentNode = ref<any>(null)

// 表单数据
const formData = reactive({
  Id: '',
  Name: '',
  Code: '',
  IconNew: '',
  Icon: '',
  Func: '',
  OrderSort: 0,
  IsButton: false,
  IsHide: false,
  IskeepAlive: false,
  Enabled: true,
  Description: '',
  Pid: '0',
  Mid: ''
})

// 接口列表
const interfaceList = ref<any[]>([])

// 加载接口列表
const loadInterfaceList = async () => {
  try {
    const res: any = await PermissionManagement.interfaceList(
      { page: 1, intPageSize: 9999 },
      []
    )
    if (res && res.status === 200 && res.success) {
      interfaceList.value = res.response?.data || []
    }
  } catch (error) {
    console.error('加载接口列表失败:', error)
  }
}

const handleInterfaceSelectVisibleChange = (visible: boolean) => {
  if (visible) {
    loadInterfaceList()
  }
}

// 对话框标题
const dialogTitle = computed(() => {
  if (dialogMode.value === 'create') return '添加根菜单'
  if (dialogMode.value === 'addChild') return '添加子菜单'
  return '编辑菜单'
})

// 菜单类型
type MenuType = '目录' | '页面' | '按钮'
const menuType = ref<MenuType>('目录')

// 表单规则
const formRules = computed(() => ({
  Name: [{ required: true, message: menuType.value === '按钮' ? '请输入按钮名称' : '请输入菜单名称', trigger: 'blur' }]
}))

const applyMenuTypeToForm = (val: MenuType) => {
  if (val === '目录') {
    formData.IsButton = false
    formData.Code = ''
  } else if (val === '按钮') {
    formData.IsButton = true
    formData.Code = ''
  } else {
    formData.IsButton = false
  }
}

// 根据菜单类型计算控制
watch(menuType, (val) => {
  applyMenuTypeToForm(val)
}, { immediate: true })

// 菜单路由是否禁用（目录和按钮时禁用）
const isCodeDisabled = computed(() => {
  return menuType.value === '目录' || menuType.value === '按钮'
})

// 菜单路由验证规则（页面时必填，且必须以/开头）
const codeRules = computed(() => {
  if (menuType.value !== '页面') return []
  return [
    { required: true, message: '请输入页面组件路径', trigger: 'blur' },
    { pattern: /^\//, message: '组件路径必须以 / 开头', trigger: 'blur' }
  ]
})

// 图标选项列表
const iconOptions = computed(() => ElementPlusIconsVue)

// 获取图标组件
const getIconComponent = (iconName: string) => {
  return (ElementPlusIconsVue as any)[iconName] || 'CircleCheck'
}

// 获取菜单类型文本
const getMenuTypeText = (row: any) => {
  if (row.IsButton) return '按钮'
  if (row.Code) return '页面'
  return '目录'
}

// 获取菜单类型标签样式
const getMenuTypeType = (row: any) => {
  if (row.IsButton) return 'warning'
  if (row.Code) return 'success'
  return 'primary'
}

// 规范化树节点结构
const normalizeTreeNodes = (nodes: any[]) => {
  nodes.forEach((item: any) => {
    const hasChildren =
      item.hasChildren ?? item.HasChildren ?? item.haschildren ?? false
    item.hasChildren = Boolean(hasChildren)
    if (Array.isArray(item.Children) && item.Children.length > 0) {
      item.hasChildren = true
      normalizeTreeNodes(item.Children)
    }
  })
}

// 根据ID在当前树中查找节点
const findNodeById = (nodes: any[], id: string | number): any | null => {
  const targetId = String(id)
  for (const node of nodes) {
    if (String(node.Id) === targetId) return node
    if (Array.isArray(node.Children) && node.Children.length > 0) {
      const found = findNodeById(node.Children, id)
      if (found) return found
    }
  }
  return null
}

const mergeMenuNodes = (oldNodes: any[] = [], newNodes: any[] = []) => {
  return newNodes.map((newNode: any) => {
    const oldNode = oldNodes.find((item: any) => String(item.Id) === String(newNode.Id))
    if (!oldNode) return newNode

    const oldChildren = Array.isArray(oldNode.Children) ? oldNode.Children : []
    const newChildren = Array.isArray(newNode.Children) ? newNode.Children : []
    const children = oldChildren.length > 0
      ? mergeMenuNodes(oldChildren, newChildren.length > 0 ? newChildren : oldChildren)
      : newChildren

    return {
      ...oldNode,
      ...newNode,
      hasChildren: Boolean(
        newNode.hasChildren ||
        newNode.HasChildren ||
        newNode.haschildren ||
        oldNode.hasChildren ||
        children.length > 0
      ),
      Children: children
    }
  })
}

// 合并根节点数据，尽量保留当前树里已经展开/已加载的子节点
const mergeRootNodes = (oldNodes: any[], newNodes: any[]) => mergeMenuNodes(oldNodes, newNodes)

const syncLazyChildren = (parentId: string | number, children: any[]) => {
  const table = tableRef.value
  const keyCandidates = Array.from(new Set([
    parentId,
    String(parentId),
    Number.isNaN(Number(parentId)) ? parentId : Number(parentId)
  ]))

  if (table?.updateKeyChildren) {
    keyCandidates.forEach((key) => {
      try {
        table.updateKeyChildren(key, children)
      } catch (error) {
        console.warn('同步菜单子节点失败:', key, error)
      }
    })
  }

  const states = table?.store?.states
  const lazyTreeNodeMap = states?.lazyTreeNodeMap?.value || states?.lazyTreeNodeMap
  const treeData = states?.treeData?.value || states?.treeData

  keyCandidates.forEach((key) => {
    if (lazyTreeNodeMap) {
      lazyTreeNodeMap[key as any] = children
    }
    if (treeData?.[key as any]) {
      treeData[key as any] = {
        ...treeData[key as any],
        expanded: true,
        loaded: true,
        loading: false,
        children: children.map((item: any) => item.Id)
      }
    }
  })
}

const expandMenuRow = (row: any) => {
  if (!row || !tableRef.value?.toggleRowExpansion) return
  try {
    tableRef.value.toggleRowExpansion(row, true)
  } catch (error) {
    console.warn('展开菜单节点失败:', error)
  }
}

// 查询某个父菜单ID下的子菜单列表
const queryMenuByParentId = async (parentId: string | number) => {
  const res: any = await PermissionManagement.getMenuTreeTable({
    Id: parentId,
    key: searchKey.value || undefined
  })
  if (res && res.status === 200 && res.success) {
    const data = res.response || []
    normalizeTreeNodes(data)
    return data
  }
  return []
}

// 加载根节点数据
const loadRootData = async () => {
  try {
    loading.value = true
    currentRefreshParentId.value = 0
    menuTree.value = await queryMenuByParentId(0)
  } catch (error) {
    console.error('加载数据失败:', error)
    menuTree.value = []
  } finally {
    loading.value = false
  }
}

// 懒加载子节点
const load = async (row: any, treeNode: any, resolve: (data: any[]) => void) => {
  try {
    currentRefreshParentId.value = row.Id
    const data = await queryMenuByParentId(row.Id)
    row.Children = data
    row.hasChildren = data.length > 0
    resolve(data)
  } catch (error) {
    console.error('加载子节点失败:', error)
    resolve([])
  }
}

// 监听搜索
watch(searchKey, () => {
  currentRefreshParentId.value = 0
  loadRootData()
})

// 按父菜单ID刷新数据：
// 1) 先刷新接口列表（与页面初始化一致）
// 2) 一定刷新根菜单(Id=0)
// 3) 若有父菜单上下文，再刷新该父菜单下最新子菜单
const refreshTable = async (parentId?: string | number) => {
  const targetParentId = parentId ?? currentRefreshParentId.value ?? 0
  const targetParentIdStr = String(targetParentId)

  try {
    loading.value = true
    await loadInterfaceList()

    // 先刷新根菜单数据，但保留当前树里已展开分支，避免子节点显示被重置
    const latestRootNodes = await queryMenuByParentId(0)
    const nextMenuTree = mergeRootNodes(menuTree.value, latestRootNodes)

    // 先提交根节点的新数据，再操作 el-table 的懒加载缓存；否则缓存会指向旧树分支。
    menuTree.value = [...nextMenuTree]
    await nextTick()

    // 若当前在某个父菜单上下文，再额外刷新该父菜单下子菜单并展开本次操作的父节点。
    if (targetParentIdStr !== '0') {
      const latestChildren = await queryMenuByParentId(targetParentId)
      const parentNode = findNodeById(menuTree.value, targetParentId)
      if (parentNode) {
        parentNode.Children = latestChildren
        parentNode.hasChildren = latestChildren.length > 0
        menuTree.value = [...menuTree.value]
        await nextTick()
        expandMenuRow(parentNode)
        syncLazyChildren(targetParentId, latestChildren)
        currentRefreshParentId.value = targetParentId
      } else {
        currentRefreshParentId.value = 0
      }
    } else {
      currentRefreshParentId.value = 0
    }
  } catch (error) {
    console.error('刷新数据失败:', error)
  } finally {
    loading.value = false
  }
}

// 添加根菜单
const handleAddRoot = () => {
  dialogMode.value = 'create'
  resetForm()
  menuType.value = '目录'
  applyMenuTypeToForm(menuType.value)
  currentNode.value = null
  formData.Pid = '0'
  formData.Mid = ''
  currentRefreshParentId.value = 0
  dialogVisible.value = true
}

// 添加子菜单
const handleAddChild = (data: any) => {
  dialogMode.value = 'addChild'
  resetForm()
  menuType.value = '目录'
  applyMenuTypeToForm(menuType.value)
  formData.Pid = data.Id
  formData.Mid = data.Mid || ''
  currentNode.value = data
  currentRefreshParentId.value = data.Id
  dialogVisible.value = true
}

// 删除菜单
const handleDelete = (data: any) => {
  ElMessageBox.confirm(`确定要删除菜单 "${data.Name}" 吗？`, '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      const res: any = await PermissionManagement.deleteMenu({ id: data.Id })
      if (res && res.status === 200 && res.success) {
        ElMessage.success('删除成功')
        const parentId = data?.Pid ?? 0
        await refreshTable(parentId)
      } else {
        ElMessage.error(res?.msg || '删除失败')
      }
    } catch (error) {
      console.error('删除菜单失败:', error)
      ElMessage.error('删除失败')
    }
  }).catch(() => {
    // 用户取消删除
  })
}

// 编辑菜单
const handleEdit = (data: any) => {
  dialogMode.value = 'edit'
  currentNode.value = data
  // 填充表单
  Object.assign(formData, data)
  // 根据数据推断菜单类型
  const nextMenuType: MenuType = data.IsButton
    ? '按钮'
    : data.Code
      ? '页面'
      : '目录'
  menuType.value = nextMenuType
  if (nextMenuType === '按钮') {
    menuType.value = '按钮'
    formData.IsButton = true
    formData.Code = ''
  } else if (nextMenuType === '页面') {
    formData.IsButton = false
    formData.Code = data.Code || ''
  } else {
    formData.IsButton = false
    formData.Code = ''
  }
  dialogVisible.value = true
}

// 重置表单
const resetForm = () => {
  formRef.value?.clearValidate?.()
  formData.Id = ''
  formData.Name = ''
  formData.Code = ''
  formData.IconNew = ''
  formData.Icon = ''
  formData.Func = ''
  formData.OrderSort = 0
  formData.IsButton = false
  formData.IsHide = false
  formData.IskeepAlive = false
  formData.Enabled = true
  formData.Description = ''
  formData.Pid = '0'
  formData.Mid = ''
}

// 提交表单
const handleSubmit = () => {
  formRef.value?.validate(async (valid: boolean) => {
    if (!valid) return
    try {
      saving.value = true
      applyMenuTypeToForm(menuType.value)
      const isCreate = dialogMode.value === 'create' || dialogMode.value === 'addChild'
      const res: any = isCreate
        ? await PermissionManagement.createMenu({ ...formData })
        : await PermissionManagement.updateMenu({ ...formData })
      if (res && res.status === 200 && res.success) {
        ElMessage.success(isCreate ? '创建成功' : '更新成功')
        dialogVisible.value = false
        const refreshParentId = isCreate
          ? (formData.Pid || 0)
          : (currentNode.value?.Pid ?? formData.Pid ?? 0)
        await refreshTable(refreshParentId)
      } else {
        ElMessage.error(res?.msg || (isCreate ? '创建失败' : '更新失败'))
      }
    } catch (error) {
      console.error('保存菜单失败:', error)
      ElMessage.error('保存失败')
    } finally {
      saving.value = false
    }
  })
}

// 挂载时加载数据
onMounted(() => {
  loadInterfaceList()
  loadRootData()
})
</script>

<style scoped lang="less">
.menu-management {
  .page-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .page-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin: 0;
    }
  }

  .tree-container {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    .tree-toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 12px;

      .search-input {
        flex: 1;
      }

      .toolbar-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }

    .tree-scrollbar {
      flex: 1;
      min-height: 0;
    }
  }

  .table-cell-name {
    display: inline-flex;
    align-items: center;
    gap: 8px;

    .cell-icon {
      font-size: 16px;
    }

    .cell-tag {
      margin-left: 4px;
    }
  }

  .cell-route {
    color: #606266;
    font-size: 13px;
  }

  :deep(.el-table) {
    .el-table__expand-icon {
      color: #409eff;
    }
  }
}

.w-full {
  width: 100%;
}
</style>
